import { templateLicenseMonthlySubscriptionSubForm } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { LicenseMonthlySubscriptionSubFormWebComponent } from '../component/LicenseMonthlySubscriptionSubFormWebComponent';
import { LicenseMonthlySubscriptionSubFormView } from '../view/LicenseMonthlySubscriptionSubFormView';
import { isHidden, toggleVisibility } from '../../../utils/htmlElement.utils';
import { MonthlyProductType } from '../../types/MonthlyProductType';
import { InputEvents } from '../../../ui/gzInput/types';
import { ToggleEvents } from '../../../ui/gzToggle/types';
import { CompanyType } from '../../../company/types/company-type';
import { FormEvent } from '../../../ui/gzForm/formField/types/form-event.enum';
import { LicenseMainFormViewData } from '../../licenseMainForm/types/LicenseMainFormViewData';
import { ServicesFormEvent } from '../../common/servicesForm/types/ServicesFormEvent';
import { AddonFormEvent } from '../../common/addonsForm/types/AddonFormEvent';
import { AddonFormType } from '../../common/addonsForm/types/AddonFormType';
import { CompanyData } from '../../../company/types/company-data';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';
import { DisableReservedSeatsOptionConfirmationWindow } from '../../../company/common/disableReservedSeatsOptionConfirmationWindow/DisableReservedSeatsOptionConfirmationWindow';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { FormGroupValue } from '../../../ui/gzForm/formGroup/types/form-group-value';
import { LicenseMonthlySubscriptionSubFormEvents } from '../types/LicenseMonthlySubscriptionSubFormEvents';
import { GzToggleWebComponent } from '../../../ui/gzToggle';
import { WEB_COMPONENT_GZ_TOGGLE } from '../../../webcomponents.constants';
import {
  Feature,
  FeatureFlag,
  FeaturesPartOfEdrDelegate,
  FeaturesPartOfEdrUsable,
  FeaturesPartOfXdrUsable,
} from '@nirvana/shared/constants';
import { AvailableBundles, Bundle } from '../../types/Bundles';
import _ from 'lodash';
import { ProductServiceStatus } from '../../types/ProductServiceStatus';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { AvailableFeaturesForTrial } from '../../../company/common/mspInProductTrialsTab/types/msp-in-product-trials-types';

export class LicenseMonthlySubscriptionSubFormController extends BaseWebComponentController {
  constructor(component: LicenseMonthlySubscriptionSubFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: LicenseMonthlySubscriptionSubFormView,
      viewTemplate: templateLicenseMonthlySubscriptionSubForm,
    });
  }

  public component: LicenseMonthlySubscriptionSubFormWebComponent;
  public view: LicenseMonthlySubscriptionSubFormView;
  public viewConstructor: typeof LicenseMonthlySubscriptionSubFormView;

  private canBeManagedByAbove: boolean;
  private childCompanyType: CompanyType;
  private selectedUsableProduct: MonthlyProductType;

  private isDisableReservedSeatsOptionConfirmationWindowAllowed = false;

  private availableBundles: AvailableBundles | any;
  private selectedUsableBundle: number | null = null;
  private featuresInTrial: FeatureFlag[] | undefined;
  private maxSelectedBundleLevel: number | null = null;
  private maxSelectedResellBundleIsALaCarte: boolean | null = null;
  private NO_SELECTED_BUNDLE_LEVEL = -1;
  private companyData: CompanyData | null = null;

  /**
   * @param bundlesToggle
   * @private
   */
  private static enableAllSecurityBundles(bundlesToggle: GzToggleWebComponent[]): void {
    for (const bundleToggle of bundlesToggle) {
      if (bundleToggle) {
        bundleToggle.readonly = false;
        if (bundleToggle.parentNode) {
          bundleToggle.parentNode['readonly'] = false;
        }
      }
    }

    const aLaCarteResellBundle = bundlesToggle[Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE] as GzToggleWebComponent;
    if (aLaCarteResellBundle) {
      aLaCarteResellBundle.checked = true;
      if (aLaCarteResellBundle.parentNode) {
        aLaCarteResellBundle.parentNode['readonly'] = true;
      }
      aLaCarteResellBundle.readonly = true;
    }
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$ownUseProductTypeFormField,
        event: FormEvent.ValueChange,
        callback: this.onAssignedProductTypeChange,
      },
      {
        element: this.view.dom.$resellingProductEndpointSecurityToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onProductEndpointSecurityToggleChange,
      },
      {
        element: this.view.dom.$resellingProductBitdefenderEdrToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onProductBitdefenderEdrToggleChange,
      },

      // Reserve Seats toggle
      {
        element: this.view.dom.$reserveSeatsFormField,
        event: FormEvent.ToggleableChange,
        callback: this.onToggleReserveSeats,
      },
      {
        element: this.view.dom.$reserveSeatsFormField,
        event: InputEvents.CHANGE,
        callback: this.onReserveSeatsValueChange,
      },
      // End subscription date
      {
        element: this.view.dom.$subscriptionEndDateFormField,
        event: FormEvent.ToggleableChange,
        callback: this.updateAutoRenewalFormFieldAvailability,
      },

      // Bundles
      {
        element: this.view.dom.$ownUseBundleForm,
        event: 'radioOwnUseSelectionChanged',
        callback: this.bundleSelectionChangedOwnUse,
      },
      {
        element: this.view.dom.$resellBundleForm,
        event: 'toggleResellBundleChanged',
        callback: this.bundleSelectionChangedResell,
      },

      // Addon forms
      {
        element: this.view.dom.$ownUseMonthlyAddonsForm,
        event: AddonFormEvent.ViewUpdate,
        callback: this.setAddonFormsVisibility,
      },
      {
        element: this.view.dom.$resellingMonthlyAddonsForm,
        event: AddonFormEvent.ViewUpdate,
        callback: this.setAddonFormsVisibility,
      },
      {
        element: this.view.dom.$ownUseMonthlyAddonsForm,
        event: AddonFormEvent.ToggleUpdate,
        callback: this.updateResellingFormFromOwnUse,
      },
      {
        element: this.view.dom.$resellingMonthlyAddonsForm,
        event: AddonFormEvent.ToggleUpdate,
        callback: this.updateOwnUseFormFromReselling,
      },

      // Services form
      {
        element: this.view.dom.$ownUseMonthlyServicesForm,
        event: ServicesFormEvent.ViewUpdate,
        callback: this.setServicesFormVisibility,
      },
      {
        element: this.view.dom.$ownUseMonthlyServicesForm,
        event: ServicesFormEvent.MdrToggleUpdate,
        callback: this.onToggleMdrOwnUse,
      },
      {
        element: this.view.dom.$resellingMdrFormField,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onToggleMdrResell,
      },
    ];
  }

  public setCompanyType(companyType: CompanyType, isInitialValueSet: boolean) {
    this.childCompanyType = companyType;
    this.updateDelegateSection(isInitialValueSet);

    this.view.dom.$ownUseMonthlyAddonsForm.childCompanyType = companyType;
    this.view.dom.$resellingMonthlyAddonsForm.childCompanyType = companyType;

    this.applyMdrContactInformationRestrictionsForCustomers(companyType);

    if (this.childCompanyType === CompanyType.Customer) {
      this.view.mdrDelegateFormField.value = false;
    } else {
      this.view.mdrDelegateFormField.value = this.view.dom.$resellingMdrFormField.value;
    }

    const mdrOwnUseValue = this.view.dom.$ownUseMonthlyServicesForm.isMdrSet();
    if (!mdrOwnUseValue) {
      this.view.dom.$ownUseMonthlyAddonsForm.controller.updateEdrForMdr(mdrOwnUseValue);
    }
  }

  private isParentCompanyMdrContactInfoRequired(companyType: CompanyType) {
    if (companyType === CompanyType.Partner) {
      return false;
    }

    const parentMdrServiceStatus = this.view.viewData?.companyLicenseInformation.parentCompany.mdrManagerStatus ?? null;
    if (
      parentMdrServiceStatus === ProductServiceStatus.ACTIVE ||
      parentMdrServiceStatus === ProductServiceStatus.ONBOARDING
    ) {
      return false;
    }

    // company already has the feature, leave the toggle editable so that partner can disable it
    const features = this.view.viewData?.companyLicenseInformation.editedCompanyFeatures ?? [];
    if (features[Feature.PRODUCT_FEATURE_MDR_ADVICE_USABLE]) {
      return false;
    }

    return true;
  }

  private applyMdrContactInformationRestrictionsForCustomers(companyType: CompanyType) {
    const parentCompanyMdrContactInfoRequired = this.isParentCompanyMdrContactInfoRequired(companyType);

    const bundleToggles = this.getBundlesToggleOwnUseElements();
    Object.keys(this.availableBundles).forEach((key: any) => {
      const bundle = this.availableBundles[key];
      if (bundle.addons.indexOf('mdrAdviceUsable') !== -1) {
        bundleToggles[key].disabled = parentCompanyMdrContactInfoRequired;
      }
    });

    this.view.dom.$ownUseBundleForm.parentCompanyMdrContactInfoRequired = parentCompanyMdrContactInfoRequired;
  }

  public setCanBeManagedByAbove(canBeManagedByAbove: boolean): void {
    this.canBeManagedByAbove = canBeManagedByAbove;

    const mdrValue = this.view.dom.$resellingMdrFormField.value || this.view.dom.$ownUseMonthlyServicesForm.isMdrSet();

    if (mdrValue) {
      this.checkMdrManagedByAboveRestrictions();
    }
  }

  public setViewData(viewData: LicenseMainFormViewData) {
    this.view.dom.$ownUseMonthlyAddonsForm.formType = AddonFormType.OwnUse;
    this.view.dom.$resellingMonthlyAddonsForm.formType = AddonFormType.Resell;
    this.view.setViewData(viewData);
    this.featuresInTrial = this.getFeaturesInTrial();
    this.view.dom.$ownUseMonthlyAddonsForm.availableFeatures = viewData.companyLicenseInformation.toggleableFeatures;
    this.view.dom.$ownUseMonthlyAddonsForm.featuresInTrial = this.featuresInTrial;
    this.view.dom.$resellingMonthlyAddonsForm.availableFeatures = viewData.companyLicenseInformation.toggleableFeatures;
    this.view.dom.$ownUseMonthlyServicesForm.availableFeatures = viewData.companyLicenseInformation.toggleableFeatures;
    this.view.dom.$ownUseMonthlyServicesForm.featuresInTrial = this.featuresInTrial;
    this.view.dom.$ownUseMonthlyServicesForm.parentCompany = viewData.companyLicenseInformation.parentCompany;

    const mspInTrialFeatures = this.featuresInTrial;

    if (mspInTrialFeatures) {
      this.view.dom.$ownUseProductTypeFormField.disabled = mspInTrialFeatures.length !== 0;
    }

    this.availableBundles = viewData.companyLicenseInformation.availableBundles;
    this.view.dom.$ownUseBundleForm.availableBundles = this.availableBundles;
    this.view.dom.$resellBundleForm.availableBundles = this.availableBundles;

    this.view.dom.$subscriptionDetailsAccordion.isOpen = true;

    const isPartner = this.childCompanyType === CompanyType.Partner;
    this.view.dom.$ownUseAccordion.isOpen = !isPartner;

    if (!isHidden(this.view.dom.$resellingAccordion)) {
      this.view.dom.$resellingAccordion.isOpen = false;
    }
  }

  public setCompanyData(company: CompanyData) {
    this.companyData = _.cloneDeep(company);
    // Setting initial company type when editing a company
    this.setCompanyType(CompanyData.getCompanyType(company), true);

    this.view.setCompanyData(company);

    // Setting initial trigger for dialog from company data
    this.setUpDisableReservedSeatsOptionConfirmationWindow(company);

    this.canBeManagedByAbove = company.specifics.canBeManagedByAbove;

    const mdrValue = company.specifics.features.mdrAdviceUsable || company.specifics.features.mdrAdviceDelegate;
    if (mdrValue) {
      this.checkMdrManagedByAboveRestrictions();
    }
  }

  private updateDelegateSection(isInitialValueSet: boolean) {
    const isCreatingPartner = this.childCompanyType === CompanyType.Partner;

    this.view.dom.$ownUseAccordion.isOpen = !isCreatingPartner;
    toggleVisibility(this.view.dom.$resellingAccordion, isCreatingPartner);

    if (!isInitialValueSet) {
      this.view.dom.$resellingProductEndpointSecurityToggle.value =
        isCreatingPartner && this.selectedUsableProduct === MonthlyProductType.EndpointSecurity;

      this.view.dom.$resellingProductBitdefenderEdrToggle.value =
        isCreatingPartner && this.selectedUsableProduct === MonthlyProductType.BitdefenderEdr;
    }
  }

  // Event handlers

  private onAssignedProductTypeChange(event: Event): void {
    const customEvent = event as CustomEvent<{ isUserAction: boolean; value: MonthlyProductType }>;
    const isUserAction = customEvent.detail.isUserAction;

    const isPartner = this.childCompanyType === CompanyType.Partner;
    const newSelectedProduct = this.view.dom.$ownUseProductTypeFormField.value as MonthlyProductType;

    if (this.selectedUsableProduct === newSelectedProduct) {
      return;
    }

    this.selectedUsableProduct = newSelectedProduct;
    this.view.dom.$ownUseMonthlyAddonsForm.usableProduct = newSelectedProduct;
    this.view.dom.$resellingMonthlyAddonsForm.usableProduct = newSelectedProduct;
    this.view.dom.$ownUseMonthlyServicesForm.usableProduct = newSelectedProduct;

    switch (this.selectedUsableProduct) {
      case MonthlyProductType.BitdefenderEdr:
        // Force Bitdefender EDR toggle to be 'on'
        this.view.dom.$resellingProductBitdefenderEdrToggle.value = isPartner;

        if (isUserAction) {
          this.view.dom.$resellingProductEndpointSecurityToggle.value = false;
        }

        // In-memory toggle for companion usable
        this.view.companionUsableToggle.value = true;

        // Enable Endpoint Security toggle and disable Bitdefender Edr toggle
        this.view.dom.$resellingProductBitdefenderEdrToggle.readonly = true;
        this.view.dom.$resellingProductEndpointSecurityToggle.readonly = false;

        const hasMdrEnabledResell = this.view.dom.$resellingMdrFormField.value;

        if (hasMdrEnabledResell) {
          this.view.dom.$resellingMonthlyAddonsForm.controller.updateEdrForMdr(hasMdrEnabledResell);
        }

        // reset own use bundles
        this.view.dom.$ownUseBundleForm.uncheckOptions();
        this.selectedUsableBundle = null;
        this.maxSelectedBundleLevel = null;
        this.maxSelectedResellBundleIsALaCarte = null;

        //bundle enable bundles into resell
        //TODO to be moved to a separate method
        for (const bundleId of Object.values(Bundle)) {
          const toggle = this.view.dom.$resellBundleForm.shadowRoot?.querySelector(
            WEB_COMPONENT_GZ_TOGGLE + '[data-bundle-id="' + bundleId + '"]'
          ) as GzToggleWebComponent | undefined;
          if (toggle) {
            toggle.readonly = false;
            if (toggle.parentNode) {
              toggle.parentNode['readonly'] = false;
            }
            toggle.checked = false;
          }
        }
        //bundle
        break;

      case MonthlyProductType.EndpointSecurity:
        // Force Endpoint Security toggle to be 'on'
        if (isUserAction) {
          this.view.dom.$resellingProductBitdefenderEdrToggle.value = false;
        }
        this.view.dom.$resellingProductEndpointSecurityToggle.value = isPartner;

        // In-memory toggle for companion usable
        this.view.companionUsableToggle.value = false;

        // Disable Endpoint Security toggle and enable Bitdefender EDR toggle
        this.view.dom.$resellingProductBitdefenderEdrToggle.readonly = false;
        this.view.dom.$resellingProductEndpointSecurityToggle.readonly = true;

        const hasMdrEnabled = this.view.dom.$ownUseMonthlyServicesForm.isMdrSet();
        if (hasMdrEnabled) {
          this.view.dom.$ownUseMonthlyAddonsForm.controller.updateEdrForMdr(hasMdrEnabled);
        }

        this.view.dom.$ownUseBundleForm.selectFirstOption();

        break;
    }
    this.view.dom.$resellBundleForm.validate();
  }

  private onProductEndpointSecurityToggleChange() {
    this.updateDelegateProductsInAddonForms();
    this.setFirstResellBundleOptionChecked();
  }

  /**
   * Check first resell bundle option when switching on the Endpoint Security toggle only when no resell bundle is selected.
   * @private
   */
  private setFirstResellBundleOptionChecked(): void {
    if (this.view.dom.$resellingProductEndpointSecurityToggle.value) {
      const bundlesToggle = this.getBundlesToggleResellElements();
      if (bundlesToggle.length) {
        for (const bundleToggle of Object.values(bundlesToggle)) {
          if (bundleToggle.checked) {
            return;
          }
        }
        const resellFirstOption = bundlesToggle.find(element => !!element) as GzToggleWebComponent;
        resellFirstOption.checked = true;
        this.hideAndCheckAddonsDependingOnBundleForResell(bundlesToggle);
      }
    }
    this.view.dom.$resellBundleForm.validate();
  }

  private onProductBitdefenderEdrToggleChange() {
    this.updateDelegateProductsInAddonForms();
  }

  private updateDelegateProductsInAddonForms(): void {
    this.view.dom.$resellingMonthlyAddonsForm.delegableProducts = this.getDelegableProducts();
  }

  private getDelegableProducts(): MonthlyProductType[] {
    const delegableProducts: MonthlyProductType[] = [];

    if (this.childCompanyType === CompanyType.Customer) {
      return delegableProducts;
    }

    if (this.view.dom.$resellingProductEndpointSecurityToggle.value) {
      delegableProducts.push(MonthlyProductType.EndpointSecurity);
    }
    if (this.view.dom.$resellingProductBitdefenderEdrToggle.value) {
      delegableProducts.push(MonthlyProductType.BitdefenderEdr);
    }

    return delegableProducts;
  }

  private onToggleReserveSeats(event: WebComponentEvent<{ isOn: boolean; isUserAction: Boolean }>) {
    const isOn = event.detail.isOn;
    const reserveSeatsFormGroup = this.view.formGroup?.getFormChild(['specifics', 'hasReservedSlots']);

    this.view.updateMinCommitmentValidators();
    this.view.updateAvailableSeatsText();

    if (!reserveSeatsFormGroup) {
      return;
    }

    const reserveSeatsValueBeforeChange = reserveSeatsFormGroup.value;
    reserveSeatsFormGroup.value = isOn;

    const toggleChanged = reserveSeatsFormGroup.value !== reserveSeatsValueBeforeChange;
    if (toggleChanged && event.detail.isUserAction) {
      this.createAndShowDisableReservedSeatsOptionConfirmationWindowIfAllowed(isOn);
    }
  }

  private onReserveSeatsValueChange(): void {
    this.view.updateMinCommitmentValidators();
    this.view.updateAvailableSeatsText();
  }

  private updateAutoRenewalFormFieldAvailability(event: CustomEvent<{ isUserAction: boolean }>): void {
    if (!event.detail.isUserAction) {
      return;
    }

    this.view.updateAutoRenewalFormFieldAvailability();
  }

  private setAddonFormsVisibility(event: Event): void {
    const customEvent = event as CustomEvent<{
      formType: AddonFormType;
      isAnyToggleAvailable: boolean;
      usableProduct: MonthlyProductType;
    }>;
    let bundleSelectionEvent;
    if (this.selectedUsableBundle) {
      bundleSelectionEvent = event as WebComponentEvent<{ bundleId: number; checked: boolean }>;
      bundleSelectionEvent.detail.bundleId = this.selectedUsableBundle;
      bundleSelectionEvent.detail.checked = true;
    }

    if (customEvent.detail.formType === AddonFormType.OwnUse) {
      if (bundleSelectionEvent) {
        this.bundleSelectionChangedOwnUse(bundleSelectionEvent);
      }
      if (customEvent.detail.usableProduct === MonthlyProductType.BitdefenderEdr) {
        const edrUiToggle =
          this.view.dom.$ownUseMonthlyAddonsForm.controller.view.addonFields.endpointDetectionAndResponse.$uiToggle;

        toggleVisibility(edrUiToggle, true);

        if (!edrUiToggle.value) {
          edrUiToggle.value = true;
        }
        edrUiToggle.disabled = true;

        this.view.dom.$ownUseMonthlyAddonsForm.controller.view.dom.$edrRetentionAddons.setEnabled(true);
        toggleVisibility(this.view.dom.$ownUseAddonsSection, true);
      } else {
        toggleVisibility(this.view.dom.$ownUseAddonsSection, customEvent.detail.isAnyToggleAvailable);
      }
      toggleVisibility(
        this.view.dom.$ownUseBundlesSection,
        customEvent.detail.usableProduct === MonthlyProductType.EndpointSecurity
      );
    } else {
      toggleVisibility(this.view.dom.$resellingAddonsSection, customEvent.detail.isAnyToggleAvailable);
      toggleVisibility(
        this.view.dom.$resellingBundlesSection,
        this.view.dom.$resellingProductEndpointSecurityToggle.value
      );
      const bundlesToggle = this.getBundlesToggleResellElements();
      if (bundleSelectionEvent && this.getMaxSelectedResellBundleLevel(bundlesToggle) !== this.maxSelectedBundleLevel) {
        this.bundleSelectionChangedResell(bundleSelectionEvent);
      } else {
        if (bundlesToggle.length) {
          this.hideAndCheckAddonsDependingOnBundleForResell(bundlesToggle);
        }
      }
      this.view.dom.$resellBundleForm.disable = !this.view.dom.$resellingProductEndpointSecurityToggle.value;
      this.view.dom.$resellBundleForm.validate();
    }
  }

  private updateResellingFormFromOwnUse(event: Event): void {
    const customEvent = event as CustomEvent<{ addonKey: string; value: boolean }>;

    this.view.dom.$resellingMonthlyAddonsForm.controller.updateToggle(
      customEvent.detail.addonKey,
      customEvent.detail.value
    );
  }

  private updateOwnUseFormFromReselling(event: Event): void {
    const customEvent = event as CustomEvent<{ addonKey: string; value: boolean }>;

    this.view.dom.$ownUseMonthlyAddonsForm.controller.updateToggle(
      customEvent.detail.addonKey,
      customEvent.detail.value
    );
  }

  private setServicesFormVisibility(event: Event): void {
    const customEvent = event as CustomEvent<{
      isAnyServiceAvailableForResell: boolean;
      isAnyServiceAvailable: boolean;
    }>;

    toggleVisibility(this.view.dom.$ownUseServicesSection, customEvent.detail.isAnyServiceAvailable);
    toggleVisibility(this.view.dom.$resellingServicesSection, customEvent.detail.isAnyServiceAvailableForResell);
  }

  private checkMdrManagedByAboveRestrictions() {
    if (this.canBeManagedByAbove) {
      return;
    }

    const dialogConfirmCallback = async () => {
      const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

      if (confirmBtn) {
        confirmBtn.showSpinner = true;
      }

      this.component.dispatchEvent(
        new CustomEvent(LicenseMonthlySubscriptionSubFormEvents.CanBeManagedByAbove, {
          bubbles: true,
          composed: true,
        })
      );

      dialog.close();
    };

    const dialogCancellationCallback = async () => {
      const cancelBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#cancel');

      if (cancelBtn) {
        cancelBtn.showSpinner = true;
      }

      const formValue: FormGroupValue = this.component.value;
      formValue.mdrAdviceUsable = false;
      formValue.mdrAdviceDelegate = false;
      this.view.dom.$ownUseMonthlyServicesForm.value = formValue;
      this.view.dom.$resellingMdrFormField.value = false;

      dialog.close();
    };

    const dialog = this.view.createMdrDialog(dialogConfirmCallback, dialogCancellationCallback);
    const acceptBtn = dialog.footerContent?.querySelector<GzButtonWebComponent>('#confirm');

    if (acceptBtn) {
      toggleVisibility(acceptBtn, this.component.context === CompanyActionContext.Create);
    }
  }

  private onToggleMdrOwnUse(event: Event): void {
    const customEvent = event as CustomEvent<{ mdrValue: boolean }>;
    const value = customEvent.detail.mdrValue;
    this.view.dom.$ownUseMonthlyAddonsForm.controller.updateEdrForMdr(value);

    if (value) {
      this.checkMdrManagedByAboveRestrictions();
    }
  }

  private onToggleMdrResell(): void {
    const value = this.view.dom.$resellingMdrFormField.value;

    this.view.mdrDelegateFormField.value = this.childCompanyType === CompanyType.Customer ? false : value;

    this.view.dom.$resellingMonthlyAddonsForm.controller.updateEdrForMdr(value);

    if (value) {
      this.checkMdrManagedByAboveRestrictions();
    }
  }

  private setUpDisableReservedSeatsOptionConfirmationWindow(company: CompanyData) {
    if (
      this.view.viewData?.context !== CompanyActionContext.Edit ||
      !CompanyData.hasReservedSeatsForUnderneathCompanies(company)
    ) {
      return;
    }
    this.isDisableReservedSeatsOptionConfirmationWindowAllowed =
      CompanyData.hasReservedSeatsForUnderneathCompanies(company);
  }

  private createAndShowDisableReservedSeatsOptionConfirmationWindowIfAllowed(reservedSeatsToggleIsOn: boolean) {
    if (reservedSeatsToggleIsOn || !this.isDisableReservedSeatsOptionConfirmationWindowAllowed) {
      // Set the field agreeRemoveReservedSlotsForChildren to default value false if
      // is not the case to remove reserved seats either because the toggle is switched on or confirmation is not allowed
      this.view.setAgreeRemoveReservedSlotsForChildren(false);
      return;
    }

    const cancelCallback = () => {
      this.view.setAgreeRemoveReservedSlotsForChildren(false);
      this.view.updateReservedSeatsFormFieldAvailability(!reservedSeatsToggleIsOn);
    };

    const confirmCallback = () => {
      this.view.setAgreeRemoveReservedSlotsForChildren(true);
    };

    DisableReservedSeatsOptionConfirmationWindow.showDisableReservedSeatsOptionConfirmationWindow(
      this.dependencies.translateFn,
      cancelCallback,
      confirmCallback
    );
  }

  private bundleSelectionChangedOwnUse(event: WebComponentEvent): void {
    const selectedBundleId = event.detail.bundleId;
    const previousSelectedUsableBundle = this.selectedUsableBundle;
    if (this.selectedUsableBundle !== selectedBundleId) {
      this.selectedUsableBundle = selectedBundleId;
    }
    if (
      selectedBundleId !== previousSelectedUsableBundle &&
      previousSelectedUsableBundle === Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE
    ) {
      this.resetFeaturesInTrial();
    }

    let featuresPartOfCurrentOwnUseBundle = this.availableBundles[selectedBundleId].addons;
    const featuresToBeHidden = [
      ...featuresPartOfCurrentOwnUseBundle,
      ...this.availableBundles[selectedBundleId].incompatibleAddons,
    ];

    // partners should not have this service (for now)
    if (this.childCompanyType === CompanyType.Partner) {
      featuresToBeHidden.push(FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE);
      featuresPartOfCurrentOwnUseBundle = featuresPartOfCurrentOwnUseBundle.filter(
        (feature: FeatureFlag) =>
          feature !== FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE &&
          feature !== FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_VISIBLE
      );
    }

    this.view.dom.$ownUseMonthlyAddonsForm.hideAddons(featuresToBeHidden);
    this.view.dom.$ownUseMonthlyAddonsForm.setBundleId(selectedBundleId);

    if (selectedBundleId !== Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE) {
      toggleVisibility(
        this.view.dom.$ownUseMonthlyAddonsForm.controller.view.featureFlagToAddonFieldMap[FeatureFlag.PRODUCT_FEATURE_EDR_USABLE].$uiToggle,
        true
      );
      this.view.dom.$ownUseMonthlyAddonsForm.controller.view.featureFlagToAddonFieldMap[
        FeatureFlag.PRODUCT_FEATURE_EDR_USABLE
      ].$uiToggle.disabled = true;
    } else {
      this.view.dom.$ownUseMonthlyAddonsForm.controller.view.featureFlagToAddonFieldMap[
        FeatureFlag.PRODUCT_FEATURE_EDR_USABLE
      ].$uiToggle.disabled = this.view.dom.$ownUseMonthlyServicesForm.isMdrSet() || (this.featuresInTrial ?? {})[FeatureFlag.PRODUCT_FEATURE_EDR_USABLE];
    }

    const isAnyOwnUseAddonVisible = this.view.dom.$ownUseMonthlyAddonsForm.isAnyAddonVisible();
    toggleVisibility(this.view.dom.$ownUseAddonsSection, isAnyOwnUseAddonVisible);
    this.view.dom.$ownUseMonthlyServicesForm.hideServices(featuresToBeHidden);
    this.view.dom.$ownUseMonthlyAddonsForm.setCheckedValueForFeatureAddons(
      this.availableBundles[selectedBundleId].incompatibleAddons,
      false
    );
    if (previousSelectedUsableBundle !== null && previousSelectedUsableBundle !== selectedBundleId) {
      this.uncheckAddonsForOwnUsePartOfPreviousSelectedBundle(previousSelectedUsableBundle);
    }

    // check addons/services part of the selected bundle
    this.view.dom.$ownUseMonthlyServicesForm.setCheckedValueForServices(featuresPartOfCurrentOwnUseBundle, true);
    this.view.dom.$ownUseMonthlyAddonsForm.setCheckedValueForFeatureAddons(featuresPartOfCurrentOwnUseBundle, true);

    if (this.childCompanyType === CompanyType.Customer) {
      if (
        selectedBundleId !== previousSelectedUsableBundle &&
        previousSelectedUsableBundle &&
        selectedBundleId === Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE
      ) {
        this.restoreFeaturesInTrial();
      }
      return;
    }

    // select same bundle on resell
    const resellBundleToggle = this.view.dom.$resellBundleForm.shadowRoot?.querySelector(
      WEB_COMPONENT_GZ_TOGGLE + '[data-bundle-id="' + selectedBundleId + '"]'
    ) as GzToggleWebComponent | undefined;
    if (!resellBundleToggle) {
      return;
    }

    // modify event in order to perform resel bundle changes
    event.detail.checked = true;
    event.detail.ownUseInteraction = true;
    this.bundleSelectionChangedResell(event);
  }

  private getBundlesToggleResellElements(): GzToggleWebComponent[] {
    return this.view.getBundlesToggleResellElements();
  }

  private getBundlesToggleOwnUseElements(): GzToggleWebComponent[] {
    return this.view.getBundlesToggleOwnUseElements();
  }

  private bundleSelectionChangedResell(event: WebComponentEvent): void {
    // create toggle resell list
    const bundlesToggle = this.getBundlesToggleResellElements();
    if (!bundlesToggle?.length) {
      return;
    }

    this.toggleBundlesOnResell(event, bundlesToggle);
    this.hideAndCheckAddonsDependingOnBundleForResell(bundlesToggle);

    //in case the company has Secure + MDR and switch to A la carte we need to enable and check the EDR toggle
    if (this.view.dom.$resellingMdrFormField.value) {
      this.view.dom.$resellingMonthlyAddonsForm.controller.updateEdrForMdr(true);
    }
  }

  private hideAndCheckAddonsDependingOnBundleForResell(bundlesToggle: GzToggleWebComponent[]): void {
    const maxSelectedBundleLevel = this.getMaxSelectedResellBundleLevel(bundlesToggle);
    const justAlaCarteSelectedOnResell =
      maxSelectedBundleLevel === this.NO_SELECTED_BUNDLE_LEVEL &&
      bundlesToggle[Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE]?.checked;

    // stop if there is no item selected
    if (!justAlaCarteSelectedOnResell && maxSelectedBundleLevel === this.NO_SELECTED_BUNDLE_LEVEL) {
      return;
    }

    const previousMaxSelectedBundleIsALaCarte = this.maxSelectedResellBundleIsALaCarte;
    this.maxSelectedResellBundleIsALaCarte = justAlaCarteSelectedOnResell;
    const previousMaxSelectedBundleLevel = this.maxSelectedBundleLevel;
    this.maxSelectedBundleLevel = maxSelectedBundleLevel;

    // hide services depending on delegate addons
    const featuresPartOfCurrentResellBundle = this.getDelegateAddonsForMaxLevelBundle(
      maxSelectedBundleLevel,
      justAlaCarteSelectedOnResell
    );
    let featuresToBeHidden = featuresPartOfCurrentResellBundle;
    const aLaCarteToggleOnResell = this.view.dom.$resellBundleForm.shadowRoot?.querySelector(
      WEB_COMPONENT_GZ_TOGGLE + '[data-bundle-id="' + Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE + '"]'
    ) as GzToggleWebComponent | undefined;
    if (!aLaCarteToggleOnResell || !aLaCarteToggleOnResell?.checked || justAlaCarteSelectedOnResell) {
      // hide incompatible Addons in case that À la carte toggle option is not checked or is the only one checked
      featuresToBeHidden = [
        ...featuresToBeHidden,
        ...this.getIncompatibleAddonsForMaxLevelBundle(maxSelectedBundleLevel, justAlaCarteSelectedOnResell),
      ];
    }

    if (previousMaxSelectedBundleLevel !== null) {
      this.uncheckAddonsOnResellPartOfPreviousSelectedBundle(
        previousMaxSelectedBundleLevel,
        previousMaxSelectedBundleIsALaCarte as boolean,
        aLaCarteToggleOnResell as GzToggleWebComponent
      );
    }

    this.view.dom.$resellingMonthlyAddonsForm.hideAddons(featuresToBeHidden);
    // check addons/services part of the selected bundle
    if (featuresPartOfCurrentResellBundle.includes(FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE_DELEGATE)) {
      this.view.dom.$resellingMdrFormField.value = true;
    }
    this.view.dom.$resellingMonthlyAddonsForm.setCheckedValueForFeatureAddons(featuresPartOfCurrentResellBundle, true);

    const isResellingServicesSectionVisible =
      this.view.dom.$ownUseMonthlyServicesForm.hasServicesAvailable() &&
      !featuresToBeHidden.includes(FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE_DELEGATE);
    toggleVisibility(this.view.dom.$resellingServicesSection, isResellingServicesSectionVisible);

    const isResellingAddonsSectionVisible = this.view.dom.$resellingMonthlyAddonsForm.isAnyAddonVisible();
    toggleVisibility(this.view.dom.$resellingAddonsSection, isResellingAddonsSectionVisible);
  }

  private toggleBundlesOnResell(event: WebComponentEvent, bundlesToggle: GzToggleWebComponent[]): void {
    const selectedBundleId = Number(event.detail.bundleId);
    const selectedBundleValue = event.detail.checked;
    const selectedOwnUseId = this.getBundleIdByFeatureFlag(this.view.dom.$ownUseBundleForm.selectedOption);

    // Enable all security bundles if company has À la carte for OWN USE && for resell
    if (selectedBundleId === Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE) {
      if (selectedOwnUseId === Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE) {
        LicenseMonthlySubscriptionSubFormController.enableAllSecurityBundles(bundlesToggle);
      }
      this.view.dom.$resellBundleForm.validate();
      return;
    }

    const selectedBundleLevel = this.availableBundles[selectedBundleId].level;
    let selectedOwnUseLevel = this.NO_SELECTED_BUNDLE_LEVEL;
    if (this.availableBundles[selectedOwnUseId]) {
      selectedOwnUseLevel = this.availableBundles[selectedOwnUseId].level;
    }
    const selectedOwnUseIsALaCarte = selectedOwnUseId === Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE;

    if (selectedBundleValue) {
      // check and disable lower bundles
      this.disableLowerLevelBundles(selectedBundleLevel, bundlesToggle, selectedOwnUseIsALaCarte, selectedOwnUseLevel);
    }

    const bundleToggleComponent = bundlesToggle[selectedBundleId] as GzToggleWebComponent;
    // check / uncheck the selected bundle
    bundleToggleComponent.checked = selectedBundleValue;
    // disable selected bundle only if it has the same value as the one into own use
    const readOnly = selectedBundleValue && selectedBundleId === selectedOwnUseId;
    bundleToggleComponent.readonly = readOnly;
    if (bundleToggleComponent.parentNode) {
      bundleToggleComponent.parentNode['readonly'] = readOnly;
    }

    this.disableHigherLevelBundles(selectedBundleLevel, bundlesToggle, event.detail.ownUseInteraction);

    // remove A-la-carte readonly
    if (selectedOwnUseId !== Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE) {
      const aLaCarteResellBundle = bundlesToggle[Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE] as GzToggleWebComponent;
      this.removeALaCarteReadOnly(aLaCarteResellBundle);
    }

    this.view.dom.$resellBundleForm.validate();
  }

  /**
   * Get max selected resell bundle level, not taking in consideration A-la-carte
   * @param {GzToggleWebComponent[]} bundlesToggle
   * @returns {number}
   * @private
   */
  private getMaxSelectedResellBundleLevel(bundlesToggle: GzToggleWebComponent[]): number {
    let maxSelectedBundleLevel = this.NO_SELECTED_BUNDLE_LEVEL;
    for (const [bundleIndex, bundleToggle] of Object.entries(bundlesToggle)) {
      if (Number(bundleIndex) === Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE) {
        continue;
      }

      if (!bundleToggle.checked) {
        continue;
      }

      if (this.availableBundles[bundleIndex].level > maxSelectedBundleLevel) {
        maxSelectedBundleLevel = this.availableBundles[bundleIndex].level;
      }
    }

    return maxSelectedBundleLevel;
  }

  private disableLowerLevelBundles(
    level: number,
    bundlesToggle: GzToggleWebComponent[],
    selectedOwnUseIsALaCarte: boolean,
    selectedOwnUseLevel: number
  ): void {
    for (const [bundleIndex, bundleToggle] of Object.entries(bundlesToggle)) {
      if (
        this.availableBundles[bundleIndex].level < level &&
        Number(bundleIndex) !== Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE
      ) {
        bundleToggle.checked = true;
        const readOnly = !selectedOwnUseIsALaCarte && this.availableBundles[bundleIndex].level <= selectedOwnUseLevel;
        bundleToggle.readonly = readOnly;
        if (bundleToggle.parentNode) {
          bundleToggle.parentNode['readonly'] = readOnly;
        }
      }
    }
  }

  private disableHigherLevelBundles(
    level: number,
    bundlesToggle: GzToggleWebComponent[],
    ownUseInteraction: boolean
  ): void {
    for (const [bundleIndex, bundleToggle] of Object.entries(bundlesToggle)) {
      if (Number(bundleIndex) === Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE) {
        continue;
      }
      if (this.availableBundles[bundleIndex].level > level) {
        bundleToggle.readonly = false;
        if (bundleToggle.parentNode) {
          bundleToggle.parentNode['readonly'] = false;
        }
        if (this.view.viewData?.context === CompanyActionContext.Create) {
          bundleToggle.checked = false;
        }

        if (this.view.viewData?.context === CompanyActionContext.Edit && !ownUseInteraction) {
          bundleToggle.checked = false;
        }
      }
    }
  }

  private getDelegateAddonsForMaxLevelBundle(
    maxLevel: number,
    maxSelectedResellBundleIsALaCarte: boolean | null
  ): FeatureFlag[] {
    if (maxSelectedResellBundleIsALaCarte) {
      return this.availableBundles[Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE].delegateAddons;
    }

    for (const availableBundleId in this.availableBundles) {
      if (availableBundleId && Number(availableBundleId) !== Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE) {
        if (this.availableBundles[availableBundleId].level === maxLevel) {
          return this.availableBundles[availableBundleId].delegateAddons;
        }
      }
    }
    return [];
  }

  private getIncompatibleAddonsForMaxLevelBundle(
    maxLevel: number,
    maxSelectedResellBundleIsALaCarte: boolean | null
  ): FeatureFlag[] {
    if (maxSelectedResellBundleIsALaCarte) {
      return this.availableBundles[Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE].incompatibleAddons;
    }
    for (const availableBundleId in this.availableBundles) {
      if (availableBundleId && Number(availableBundleId) !== Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE) {
        if (this.availableBundles[availableBundleId].level === maxLevel) {
          return this.availableBundles[availableBundleId].incompatibleAddons;
        }
      }
    }
    return [];
  }

  /**
   * Returns the bundleId based on bundleFeatureFlag
   * @param bundleFeatureFlag
   * @private
   */
  private getBundleIdByFeatureFlag(bundleFeatureFlag: FeatureFlag | undefined): Bundle {
    if (!this.availableBundles || bundleFeatureFlag === undefined) {
      return Bundle.UNDEFINED;
    }
    for (const id in this.availableBundles) {
      if (this.availableBundles.hasOwnProperty(id)) {
        const availableBundle = this.availableBundles[id];
        if (availableBundle.usableFeature === bundleFeatureFlag) {
          return Number(id);
        }
      }
    }
    return Bundle.UNDEFINED;
  }

  /**
   * @param aLaCarteResellBundle
   * @private
   */
  private removeALaCarteReadOnly(aLaCarteResellBundle: GzToggleWebComponent): void {
    if (aLaCarteResellBundle && aLaCarteResellBundle.readonly) {
      aLaCarteResellBundle.readonly = false;
      if (aLaCarteResellBundle.parentNode) {
        aLaCarteResellBundle.parentNode['readonly'] = false;
      }
      if (this.view.viewData?.context === CompanyActionContext.Create) {
        aLaCarteResellBundle.checked = false;
      }
    }
  }

  /**
   * @param previousMaxSelectedBundleLevel
   * @param previousMaxSelectedBundleIsALaCarte
   * @param aLaCarteToggleOnResell
   * @private
   */
  private uncheckAddonsOnResellPartOfPreviousSelectedBundle(
    previousMaxSelectedBundleLevel: number,
    previousMaxSelectedBundleIsALaCarte: boolean,
    aLaCarteToggleOnResell: GzToggleWebComponent
  ): void {
    let addonsToUncheck = _.clone(
      this.getDelegateAddonsForMaxLevelBundle(previousMaxSelectedBundleLevel, previousMaxSelectedBundleIsALaCarte)
    );

    // if we have xdr set, do not remove edr (even though is part of previous bundle)
    const hasXDROnResell =
      this.view.dom.$resellingMonthlyAddonsForm.controller.view.dom.$extendedDetectionAndResponseToggle.value;
    if (hasXDROnResell) {
      addonsToUncheck = addonsToUncheck.filter(function (element) {
        return !FeaturesPartOfEdrDelegate.includes(element);
      });
    }

    if (addonsToUncheck.includes(FeatureFlag.PRODUCT_FEATURE_MDR_ADVICE_USABLE_DELEGATE)) {
      this.view.dom.$resellingMdrFormField.value = false;
    }
    this.view.dom.$resellingMonthlyAddonsForm.setCheckedValueForFeatureAddons(addonsToUncheck, false);

    if (!aLaCarteToggleOnResell || !aLaCarteToggleOnResell?.checked) {
      const incompatibleAddonsToUncheck = this.getIncompatibleAddonsForMaxLevelBundle(
        previousMaxSelectedBundleLevel,
        previousMaxSelectedBundleIsALaCarte
      );
      this.view.dom.$resellingMonthlyAddonsForm.setCheckedValueForFeatureAddons(incompatibleAddonsToUncheck, false);
    }
  }

  /**
   *
   * @param previousBundleIncludedAddons
   * @private
   */
  private allCheckedXDRAddonsArePartOfPreviousBundle(previousBundleIncludedAddons: FeatureFlag[]): boolean {
    for (const feature of FeaturesPartOfXdrUsable) {
      const addonField = this.view.dom.$ownUseMonthlyAddonsForm.controller.view.featureFlagToAddonFieldMap[feature];
      if (
        !previousBundleIncludedAddons.includes(Feature.getFeatureFlag(addonField.usableFeature)) &&
        addonField.$uiToggle.value
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * @param previousSelectedUsableBundle
   * @private
   */
  private uncheckAddonsForOwnUsePartOfPreviousSelectedBundle(previousSelectedUsableBundle: number): void {
    let addonsToUncheck = _.clone(this.availableBundles[previousSelectedUsableBundle].addons);

    const allCheckedXDRAddonsArePartOfPreviousBundle = this.allCheckedXDRAddonsArePartOfPreviousBundle(addonsToUncheck);

    // if we have xdr set, do not remove edr (except if all checked xdr addons are part of the previous bundle)
    const hasXDROwnUse =
      this.view.dom.$ownUseMonthlyAddonsForm.controller.view.dom.$extendedDetectionAndResponseToggle.value;
    if (hasXDROwnUse && !allCheckedXDRAddonsArePartOfPreviousBundle) {
      addonsToUncheck = addonsToUncheck.filter(function (element) {
        return !FeaturesPartOfEdrUsable.includes(element);
      });
    }

    this.view.dom.$ownUseMonthlyServicesForm.setCheckedValueForServices(addonsToUncheck, false);
    this.view.dom.$ownUseMonthlyAddonsForm.setCheckedValueForFeatureAddons(addonsToUncheck, false);

    //in case the company has Secure + MDR and switch to A la carte we need to enable and check the EDR toggle
    const hasMdrEnabled = this.view.dom.$ownUseMonthlyServicesForm.isMdrSet();
    if (hasMdrEnabled) {
      this.view.dom.$ownUseMonthlyAddonsForm.controller.updateEdrForMdr(hasMdrEnabled);
    }
  }

  private resetFeaturesInTrial(): void {
    const mspInProductTrials = this.getFeaturesInTrial();
    if (!mspInProductTrials) {
      return;
    }

    const addons = this.view.dom.$ownUseMonthlyAddonsForm.value;
    for (const key in addons) {
      if (mspInProductTrials[key]) {
        addons[key] = false;
      }
    }
    this.view.dom.$ownUseMonthlyAddonsForm.featuresInTrial = [];
    this.view.dom.$ownUseMonthlyAddonsForm.value = addons;

    const services = this.view.dom.$ownUseMonthlyServicesForm.value;
    for (const key in services) {
      if (mspInProductTrials[key]) {
        services[key] = false;
      }
    }
    this.view.dom.$ownUseMonthlyServicesForm.featuresInTrial = [];
    this.view.dom.$ownUseMonthlyServicesForm.value = services;
    this.view.dom.$ownUseProductTypeFormField.disabled = false;
  }

  private restoreFeaturesInTrial(): void {
    const mspInProductTrials = this.getFeaturesInTrial();
    if (mspInProductTrials) {
      this.view.dom.$ownUseMonthlyAddonsForm.featuresInTrial = mspInProductTrials;
      this.view.dom.$ownUseMonthlyServicesForm.featuresInTrial = mspInProductTrials;
      const isAnyFeatureAvailable = mspInProductTrials.length !== 0;
      this.view.dom.$ownUseProductTypeFormField.disabled = isAnyFeatureAvailable;
      if (this.companyData && isAnyFeatureAvailable) {
        this.setCompanyData(this.companyData);
      }
    }
  }

  private getFeaturesInTrial(): FeatureFlag[] | undefined {
    return AvailableFeaturesForTrial.map(
      type => this.view.viewData?.company?.specifics.mspInProductTrials?.[type]?.featuresInTrial ?? []
    ).find(featuresArray => !isEmpty(featuresArray));
  }
}
